import { useState } from 'react';
import type { JobThread, KnowledgeEntry } from '../types/foreman';
import type { UserRole } from '../types/gateway';
import { JobThreadItem } from './JobThread';
import { KnowledgePanel } from './KnowledgePanel';
import styles from './Sidebar.module.css';

interface SidebarProps {
  threads: JobThread[];
  activeThreadId: string | null;
  onSelectThread: (id: string) => void;
  currentRole: UserRole;
  sidebarTab: 'threads' | 'knowledge';
  onTabChange: (tab: 'threads' | 'knowledge') => void;
  knowledgeEntries: KnowledgeEntry[];
  onSearchKnowledge: (query: string) => void;
  sidebarOpen?: boolean;
}

export function Sidebar({
  threads,
  activeThreadId,
  onSelectThread,
  currentRole: _currentRole,
  sidebarTab,
  onTabChange,
  knowledgeEntries,
  onSearchKnowledge,
  sidebarOpen = true,
}: SidebarProps) {
  const [search, setSearch] = useState('');
  const [knowledgeSearch, setKnowledgeSearch] = useState('');

  const filtered = threads.filter(
    (t) =>
      search === '' ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.code.toLowerCase().includes(search.toLowerCase())
  );

  const sidebarClass = sidebarOpen
    ? styles.sidebar
    : `${styles.sidebar} ${styles.sidebarHidden}`;

  return (
    <aside className={sidebarClass}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${sidebarTab === 'threads' ? styles.activeTab : ''}`}
          onClick={() => onTabChange('threads')}
        >
          Threads
        </button>
        <button
          className={`${styles.tab} ${sidebarTab === 'knowledge' ? styles.activeTab : ''}`}
          onClick={() => onTabChange('knowledge')}
        >
          Knowledge
        </button>
      </div>

      {sidebarTab === 'threads' && (
        <>
          <div className={styles.searchWrap}>
            <input
              className={styles.search}
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.threadList}>
            {filtered.map((thread) => (
              <JobThreadItem
                key={thread.id}
                thread={thread}
                active={thread.id === activeThreadId}
                onClick={() => onSelectThread(thread.id)}
              />
            ))}
            {filtered.length === 0 && (
              <div className={styles.empty}>No threads found</div>
            )}
          </div>
        </>
      )}

      {sidebarTab === 'knowledge' && (
        <KnowledgePanel
          entries={knowledgeEntries}
          searchQuery={knowledgeSearch}
          onSearch={(query) => {
            setKnowledgeSearch(query);
            onSearchKnowledge(query);
          }}
        />
      )}
    </aside>
  );
}
